<script setup>
import { useI18n } from 'vue-i18n';
import BillingSectionCard from './BillingSectionCard.vue';
import { formatUsdFromCents } from '../helpers/money';
import { formatBillingDateTime } from '../helpers/formatBillingTime';

defineProps({
  orders: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
});

const { t } = useI18n();

const orderTypeLabel = type => {
  const key = `CUSTOM.BILLING.ORDERS.TYPE_${String(type || '').toUpperCase()}`;
  const translated = t(key);
  return translated !== key ? translated : type;
};
</script>

<template>
  <BillingSectionCard
    :title="t('CUSTOM.BILLING.ORDERS.TITLE')"
    :description="t('CUSTOM.BILLING.ORDERS.DESCRIPTION')"
  >
    <div v-if="loading" class="text-sm text-n-slate-11">
      {{ t('CUSTOM.COMMON.LOADING') }}
    </div>
    <div v-else-if="error" class="text-sm text-n-ruby-9" role="alert">
      {{ t('CUSTOM.BILLING.ERRORS.ORDERS') }}: {{ error }}
    </div>
    <div v-else-if="!orders.length" class="text-sm text-n-slate-11">
      {{ t('CUSTOM.BILLING.ORDERS.EMPTY') }}
    </div>
    <div v-else class="overflow-x-auto rounded-lg border border-n-weak">
      <table class="w-full text-sm text-start">
        <thead class="bg-n-alpha-1 text-n-slate-11">
          <tr>
            <th class="px-3 py-2 font-medium">
              {{ t('CUSTOM.BILLING.ORDERS.COL_NUMBER') }}
            </th>
            <th class="px-3 py-2 font-medium">
              {{ t('CUSTOM.BILLING.ORDERS.COL_TYPE') }}
            </th>
            <th class="px-3 py-2 font-medium text-end">
              {{ t('CUSTOM.BILLING.ORDERS.COL_AMOUNT') }}
            </th>
            <th class="px-3 py-2 font-medium">
              {{ t('CUSTOM.BILLING.ORDERS.COL_STATUS') }}
            </th>
            <th class="px-3 py-2 font-medium">
              {{ t('CUSTOM.BILLING.ORDERS.COL_TIME') }}
            </th>
            <th class="px-3 py-2 font-medium">
              {{ t('CUSTOM.BILLING.ORDERS.COL_PAYMENT') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="order in orders"
            :key="order.id || order.order_number"
            class="border-t border-n-weak text-n-slate-12"
          >
            <td class="px-3 py-2 font-mono text-xs">
              {{ order.order_number }}
            </td>
            <td class="px-3 py-2">{{ orderTypeLabel(order.order_type) }}</td>
            <td class="px-3 py-2 text-end tabular-nums">
              {{ formatUsdFromCents(order.amount_cents) }}
            </td>
            <td class="px-3 py-2 capitalize">{{ order.status }}</td>
            <td class="px-3 py-2 whitespace-nowrap">
              {{ formatBillingDateTime(order.created_at) }}
            </td>
            <td class="px-3 py-2">
              {{
                order.payment_method === 'wallet'
                  ? t('CUSTOM.BILLING.ORDERS.PAYMENT_WALLET')
                  : order.payment_method
              }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </BillingSectionCard>
</template>
